import axios from "axios";
import { toast } from "react-toastify";

const baseURL = "https://aady-server.onrender.com/api";
// const baseURL = "http://localhost:8000/api";

const apiClient = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  (config) => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const parsedToken = JSON.parse(token);
        config.headers.Authorization = `Bearer ${parsedToken}`;
      } else {
        delete config.headers.Authorization;
      }
    } catch (error) {
      console.error("Error parsing token:", error);
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      toast.error("Session expired. Please login again.");
      window.location.href = "/admin";
    } else if (error.response?.status === 403) {
      toast.error("Access denied. You don't have permission for this action.");
    } else if (error.response?.status >= 500) {
      toast.error("Server error. Please try again later.");
    } else if (error.code === "ECONNABORTED") {
      toast.error("Request timeout. Please check your connection.");
    } else {
      toast.error(error.response?.data?.message || "An error occurred.");
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  auth: {
    login: (credentials) => apiClient.post("/sign-in", credentials),
    register: (userData) => apiClient.post("/sign-up", userData),
  },

  about: {
    get: (id) => apiClient.get(`/aboutmeget/${id}`),
    update: (id, data) => apiClient.patch(`/aboutmepatch/${id}`, data),
  },

  skills: {
    getAll: () => apiClient.get("/skillsget"),
    create: (data) => apiClient.post("/skillspost", data),
    delete: (id) => apiClient.delete(`/skillsdel/${id}`),
  },

  projects: {
    getAll: () => apiClient.get("/projectsget"),
    create: (data) => apiClient.post("/projectspost", data),
    delete: (id) => apiClient.delete(`/projectsdel/${id}`),
  },

  blog: {
    getAll: () => apiClient.get("/blogget"),
    getOne: (id) => apiClient.get(`/bloggetone/${id}`),
    create: (data) => {
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      return apiClient.post("/blogpost", data, config);
    },
    delete: (id) => apiClient.delete(`/blogdel/${id}`),
    clap: (id) => apiClient.put(`/blogclap/${id}`),
  },

  education: {
    getAll: () => apiClient.get("/eduget"),
    create: (data) => apiClient.post("/edupost", data),
    delete: (id) => apiClient.delete(`/edudel/${id}`),
  },

  messages: {
    getAll: () => apiClient.get("/msgget"),
    create: (data) => apiClient.post("/msgpost", data),
    delete: (id) => apiClient.delete(`/msgdel/${id}`),
  },

  references: {
    getAll: () => apiClient.get("/refget"),
    create: (data) => apiClient.post("/refpost", data),
    delete: (id) => apiClient.delete(`/refdel/${id}`),
  },

  userData: {
    create: (data) => apiClient.post("/ipdata", data),
    getAll: () => apiClient.get("/ipdataget"),
  },
};

export const apiUtils = {
  handleRequest: async (apiCall, options = {}) => {
    const {
      onSuccess,
      onError,
      successMessage = "Operation completed successfully",
      errorMessage = "Operation failed",
      showToast = true,
    } = options;

    try {
      const response = await apiCall();
      if (showToast && successMessage) {
        toast.success(successMessage);
      }
      if (onSuccess) {
        onSuccess(response.data);
      }
      return response.data;
    } catch (error) {
      if (showToast && errorMessage) {
        toast.error(errorMessage);
      }
      if (onError) {
        onError(error);
      }
      throw error;
    }
  },

  handleFormSubmit: async (apiCall, formData, options = {}) => {
    const {
      onSuccess,
      onError,
      successMessage = "Data saved successfully",
      errorMessage = "Failed to save data",
      resetForm,
    } = options;

    try {
      const response = await apiCall(formData);
      toast.success(successMessage);
      if (onSuccess) {
        onSuccess(response.data);
      }
      if (resetForm) {
        resetForm();
      }
      return response.data;
    } catch (error) {
      toast.error(errorMessage);
      if (onError) {
        onError(error);
      }
      throw error;
    }
  },
  
  handleDelete: async (apiCall, options = {}) => {
    const {
      onSuccess,
      onError,
      successMessage = "Item deleted successfully",
      errorMessage = "Failed to delete item",
      confirmMessage = "Are you sure you want to delete this item?",
    } = options;

    if (confirmMessage && !window.confirm(confirmMessage)) {
      return;
    }

    try {
      await apiCall();
      toast.success(successMessage);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast.error(errorMessage);
      if (onError) {
        onError(error);
      }
      throw error;
    }
  },
};

export default apiClient;
